import React from 'react';
import PropTypes from 'prop-types';
import { map, reduce } from 'lodash';
import { withPropsOnChange } from 'recompose';
import classnames from 'classnames';

import {
	storageStackedUnitBuckets,
	vehicleStackedUnitBuckets,
} from 'config/units';
import { unitPropType } from 'config/propTypes';
import pluralize from 'utils/pluralize';
import { Link } from 'components/core/Link';
import StackedUnit from './StackedUnit';

import './StackedUnits.scss';

export function emptyStorageUnit(size) {
	const unit = {
		quantity: 0,
		placeholder: true,
	};
	switch (size) {
		case 'largeUnit':
			unit.size = "10' x 10'";
			break;
		case 'mediumUnit':
			unit.size = "5' x 10'";
			break;
		default:
			unit.size = "5' x 5'";
	}

	return unit;
}

export const enhance = withPropsOnChange(
	['units', 'storageType', 'showRecommendedUnit'],
	({ facility, units, storageType, showRecommendedUnit }) => {
		let stackedUnits = [];

		if (showRecommendedUnit && units.recommendedUnit) {
			stackedUnits = [units.recommendedUnit];
		} else if (storageType === 'vehicle') {
			// Grab the first 3 available vehicle buckets
			stackedUnits = reduce(
				vehicleStackedUnitBuckets,
				(stack, bucket) =>
					units[bucket] ? [...stack, units[bucket]] : stack,
				[],
			).slice(0, 3);
		} else {
			// create size buckets with placeholders
			stackedUnits = map(
				storageStackedUnitBuckets,
				(size) => units[size] || emptyStorageUnit(size),
			);
		}

		return {
			stackedUnits,
			unitsAvailable: units.unitsAvailable,
			moreUnitsText: units.moreUnitsText,
			facilityId: facility.id,
			facilityURL: facility.url,
		};
	},
);

export function StackedUnits({
	facilityId,
	facilityURL,
	unitsAvailable,
	stackedUnits,
	withScarcity,
	className,
}) {
	return (
		<div className={classnames('stacked-units', className)}>
			{stackedUnits.map((unit, index) => (
				<StackedUnit
					key={unit.id || `${facilityId}-${index}`}
					facilityId={facilityId}
					unit={unit}
					withScarcity={withScarcity}
				/>
			))}
			<Link
				secondary
				className="view-more-units"
				to={`${facilityURL}#units`}
				productClick
			>
				Compare{' '}
				<strong>
					{unitsAvailable} {pluralize('unit', unitsAvailable)}{' '}
				</strong>{' '}
				at this facility &raquo;
			</Link>
		</div>
	);
}

StackedUnits.propTypes = {
	className: PropTypes.string,
	facilityId: PropTypes.number.isRequired,
	facilityURL: PropTypes.string,
	unitsAvailable: PropTypes.number.isRequired,
	stackedUnits: PropTypes.arrayOf(unitPropType).isRequired,
	withScarcity: PropTypes.bool,
};

export default enhance(StackedUnits);
